import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BsCheck2Circle } from "react-icons/bs";
import "./WebBuilderPricingSection.css";

function WebBuilderPricingSection() {
  const [activeTab, setActiveTab] = useState(1); // Default to Clinic (index 1) as it's most popular
  const [openAccordion, setOpenAccordion] = useState(1); // Mobile: Clinic open by default (index 1)
  // Desktop layout is finalized to "grid". Mobile remains accordion.
  const pricingLayout = "grid";

  const plans = [
    {
      key: "doctor",
      name: "Doctor (Solo Practice)",
      subtitle: "Best for: Individual doctors & specialists",
      oneTime: 35000,
      annual: 7000,
      features: [
        "Personal brand focus",
        "Professional profile showcase",
        "Multi-location Availability Setup",
        "Essential Appointment Scheduling",
        "Doctor-managed workflow",
      ],
      popular: false,
    },
    {
      key: "clinic",
      name: "Clinic (Multi-Doctor Practice)",
      subtitle: "Best for: Clinics & diagnostic centers",
      oneTime: 45000,
      annual: 9000,
      features: [
        "Facility-wide services & doctor profiles",
        "Multi-doctor coordination & scheduling",
        "Admin-assisted patient flow management",
        "SMS patient outreach & reminders",
        "Support Ticket System (help requests)",
      ],
      popular: true,
    },
    {
      key: "hospital",
      name: "Hospital (Enterprise)",
      subtitle: "Best for: Hospitals & healthcare networks",
      oneTime: 75000,
      annual: 15000,
      features: [
        "Institutional branding & leadership",
        "Permission-based system access",
        "Centralized department workflows",
        "Patient feedback management",
        "Careers & recruitment portal",
      ],
      popular: false,
    },
  ];

  const renderCard = (plan, className = "") => (
    <div className={`wb-pricing2-card ${plan.popular ? "wb-pricing2-card-popular" : ""} ${className}`}>
      {plan.popular && <div className="wb-pricing2-badge">Most Popular</div>}
      <div className="wb-pricing2-card-top">
        <h3 className="wb-pricing2-title">{plan.name}</h3>
        <p className="wb-pricing2-subtitle">{plan.subtitle}</p>
      </div>
      <div className="wb-pricing2-price">
        <div className="wb-pricing2-price-label">Setup cost</div>
        <div className="wb-pricing2-price-value">PKR {plan.oneTime.toLocaleString()}</div>
      </div>
      <ul className="wb-pricing2-list">
        {plan.features.map((feature, idx) => (
          <li key={idx}>{feature}</li>
        ))}
      </ul>
      <div className="wb-pricing2-annual">
        <span className="wb-pricing2-annual-label">Annual fee</span>
        <span className="wb-pricing2-annual-value">PKR {plan.annual.toLocaleString()}</span>
      </div>
      <div className="wb-pricing2-cta">
        <Link
          to="/request-a-demo?product=Web-Builder"
          className={`btn btn-rounded ${plan.popular ? "btn-brand-02" : "btn-outline-brand-02"} wb-pricing2-btn`}
        >
          Get Started
        </Link>
      </div>
    </div>
  );

  // Function to render pricing based on selected layout
  const renderPricingLayout = () => {
    switch (pricingLayout) {
      case 'spacious':
        return (
          <div className="wb-pricing2-spacious">
            <div className="row justify-content-center">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  {renderCard(plan, 'wb-pricing2-card-spacious')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'elegant':
        return (
          <div className="wb-pricing2-elegant">
            <div className="row justify-content-center">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  {renderCard(plan, 'wb-pricing2-card-elegant')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'modern':
        return (
          <div className="wb-pricing2-modern">
            <div className="row justify-content-center">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  {renderCard(plan, 'wb-pricing2-card-modern')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'minimal':
        return (
          <div className="wb-pricing2-minimal">
            <div className="row justify-content-center">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  {renderCard(plan, 'wb-pricing2-card-minimal')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'centered':
        return (
          <div className="wb-pricing2-centered">
            <div className="row justify-content-center">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  <div className="d-flex justify-content-center">
                    {renderCard(plan, 'wb-pricing2-card-centered')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'compact':
        return (
          <div className="wb-pricing2-compact">
            <div className="row justify-content-center">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  {renderCard(plan, 'wb-pricing2-card-compact')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'classic':
        return (
          <div className="wb-pricing2-classic">
            <div className="row justify-content-center">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  {renderCard(plan, 'wb-pricing2-card-classic')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'premium':
        return (
          <div className="wb-pricing2-premium">
            <div className="row justify-content-center">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  {renderCard(plan, 'wb-pricing2-card-premium')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'grid':
        return (
          <div className="wb-pricing2-grid-layout">
            <div className="row">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  {renderCard(plan, 'wb-pricing2-card-grid')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'card':
        return (
          <div className="wb-pricing2-card-layout">
            <div className="row justify-content-center">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  {renderCard(plan, 'wb-pricing2-card-style')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'flat':
        return (
          <div className="wb-pricing2-flat">
            <div className="row justify-content-center">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  {renderCard(plan, 'wb-pricing2-card-flat')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'rounded':
        return (
          <div className="wb-pricing2-rounded">
            <div className="row justify-content-center">
              {plans.map((plan) => (
                <div key={plan.key} className="col-lg-4 col-md-6 col-12 mb-4">
                  {renderCard(plan, 'wb-pricing2-card-rounded')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'tabs':
      default:
        return (
          <>
            <div className="wb-pricing2-tabs-nav">
              {plans.map((plan, idx) => (
                <button
                  key={plan.key}
                  type="button"
                  className={`wb-pricing2-tab-btn ${activeTab === idx ? "is-active" : ""}`}
                  onClick={() => setActiveTab(idx)}
                >
                  {plan.name}
                  {plan.popular && <span className="wb-pricing2-tab-badge">Popular</span>}
                </button>
              ))}
            </div>
            <div className="wb-pricing2-tabs-content">
              <div className="row justify-content-center wb-pricing2-cards-stack">
                {plans.map((plan, idx) => (
                  <div
                    key={plan.key}
                    className={`${activeTab === idx ? "col-12" : "col-lg-4 col-md-4"} wb-pricing2-card-wrapper ${activeTab === idx ? "is-active" : "is-inactive"}`}
                    data-plan={plan.key}
                  >
                    {renderCard(plan, "wb-pricing2-card-tab")}
                  </div>
                ))}
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <section id="pricing" className="wb-pricing2-section ptb-100 gray-light-bg plans-pricing-section">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-8 text-center">
            <div className="section-heading mb-4">
              <h2 className="web">Plans & Pricing</h2>
            </div>
          </div>
        </div>

        {/* Layout: Desktop */}
        <div className={`wb-pricing2-layout-wrapper d-none d-md-block pricing-layout-${pricingLayout}`}>
          {renderPricingLayout()}
        </div>
        {/* Disclaimer (Desktop Only, Single Place) */}
        <div className="wb-pricing2-disclaimer wb-pricing2-disclaimer--below-cards d-none d-md-block">
          <p className="wb-pricing2-disclaimer-text">
            <span className="wb-pricing2-disclaimer-icon">*</span>
            These charges exclude the charges for domain and content for the website.
          </p>
        </div>

        {/* Layout: Accordion (Mobile Only) */}
        <div className="wb-pricing2-accordion d-block d-md-none">
          {plans.map((plan, idx) => (
            <div key={plan.key} className={`wb-pricing2-accordion-item ${openAccordion === idx ? "is-open" : ""}`}>
              <button
                type="button"
                className="wb-pricing2-accordion-header"
                onClick={() => {
                  setOpenAccordion(openAccordion === idx ? null : idx);
                }}
              >
                <div className="wb-pricing2-accordion-title">
                  <span>{plan.name}</span>
                  {plan.popular && <span className="wb-pricing2-accordion-badge">Most Popular</span>}
                </div>
                <div className="wb-pricing2-accordion-price">
                  <span>PKR {plan.oneTime.toLocaleString()}</span>
                  <span className="wb-pricing2-accordion-price-annual">+ {plan.annual.toLocaleString()}/yr</span>
                </div>
                <span className="wb-pricing2-accordion-icon">+</span>
              </button>
              <div className="wb-pricing2-accordion-body">
                <div className="wb-pricing2-accordion-content">
                  <p className="wb-pricing2-accordion-subtitle">{plan.subtitle}</p>
                  <ul className="wb-pricing2-list">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx}>{feature}</li>
                    ))}
                  </ul>
                  <div className="wb-pricing2-cta">
                    <Link
                      to="/request-a-demo?product=Web-Builder"
                      className={`btn btn-rounded ${plan.popular ? "btn-brand-02" : "btn-outline-brand-02"} wb-pricing2-btn`}
                    >
                      Get Started
                    </Link>
                  </div>
                  {/* Disclaimer */}
                  <div className="wb-pricing2-disclaimer mt-3">
                    <p className="wb-pricing2-disclaimer-text">
                      <span className="wb-pricing2-disclaimer-icon">*</span>
                      These charges exclude the charges for domain and content for the website.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Add-ons Section */}
        <div className="row justify-content-center mt-1">
          <div className="col-12 col-lg-10">
            <div className="wb-pricing2-addons">
              <div className="wb-pricing2-addons-head">
                <h4 className="wb-pricing2-addons-title mb-0">Add-on Modules</h4>
                <span className="wb-pricing2-addons-subtitle text-muted">Pay-as-you-go modules</span>
              </div>

              <div className="wb-pricing2-addons-items">
                <div className="wb-pricing2-addon">
                  <div className="wb-pricing2-addon-name">Invoicing</div>
                  <div className="wb-pricing2-addon-price">PKR 5 / invoice</div>
                </div>

                <div className="wb-pricing2-addon">
                  <div className="wb-pricing2-addon-name">EMR(s)</div>
                  <div className="wb-pricing2-addon-price">PKR 2.5 / Prescription</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default WebBuilderPricingSection;

